import { useState, useMemo, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { formatCurrency } from './helpers';

/**
 * Recursive node renderer for hierarchical account trees (used by I&E and Balance Sheet).
 * Supports both naming conventions:
 *   { current_year_amount, previous_year_amount, children: [] }   (Balance Sheet)
 *   { currentTotal, previousTotal, details: [] }                  (Income & Expenditure)
 *   Plus { code | ac_code }
 */
const getCurrent = (n) => n.current_year_amount ?? n.currentTotal ?? n.currentAmount ?? n.current ?? 0;
const getPrevious = (n) => n.previous_year_amount ?? n.previousTotal ?? n.previousAmount ?? n.previous ?? 0;
const getChildren = (n) => n.children || n.details || n.accounts || n.groups || [];
const getCode = (n) => n.ac_code || n.code || n.accountCode || n.parentAccountCode;
const getName = (n) => n.name || n.parentAccountName || '';

const Row = ({ node, depth, collapsedMap, onToggle, dashOnZero }) => {
    const childNodes = getChildren(node);
    const hasChildren = childNodes.length > 0;
    const id = node.id ?? getCode(node);
    const isCollapsed = !!collapsedMap[id];
    const isLeaf = !hasChildren;

    const labelClasses = isLeaf
        ? 'tw-text-gray-700'
        : 'tw-font-semibold tw-text-gray-700';
    const amountClasses = isLeaf
        ? 'tw-text-gray-700'
        : 'tw-font-semibold tw-text-gray-800';

    return (
        <>
            <tr
                className={`tw-border-b tw-border-gray-50 ${isLeaf ? 'hover:tw-bg-slate-50/50' : 'tw-bg-gray-50/40 hover:tw-bg-gray-100/60'} tw-transition-colors`}
                onClick={hasChildren ? () => onToggle(id) : undefined}
                style={{ cursor: hasChildren ? 'pointer' : 'default' }}
            >
                <td className={`tw-px-4 tw-py-2 ${labelClasses}`} style={{ paddingLeft: `${16 + depth * 18}px` }}>
                    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                        {hasChildren ? (
                            <Icon
                                icon={isCollapsed ? 'mdi:chevron-right' : 'mdi:chevron-down'}
                                className="tw-text-gray-400 tw-text-sm"
                            />
                        ) : (
                            <Icon icon="mdi:arrow-right-thin" className="tw-text-gray-400 tw-text-sm" />
                        )}
                        <span className="tw-text-blue-700 hover:tw-underline">
                            {getName(node)}
                            {getCode(node) && depth === 0 ? ` (${getCode(node)})` : ''}
                        </span>
                    </span>
                </td>
                <td className={`tw-px-4 tw-py-2 tw-text-right tw-whitespace-nowrap ${amountClasses}`}>
                    {formatCurrency(getCurrent(node), { dashOnZero })}
                </td>
                <td className="tw-px-4 tw-py-2 tw-text-right tw-text-gray-500 tw-whitespace-nowrap">
                    {formatCurrency(getPrevious(node), { dashOnZero })}
                </td>
            </tr>
            {hasChildren && !isCollapsed && childNodes.map((child) => (
                <Row
                    key={child.id ?? getCode(child)}
                    node={child}
                    depth={depth + 1}
                    collapsedMap={collapsedMap}
                    onToggle={onToggle}
                    dashOnZero={dashOnZero}
                />
            ))}
        </>
    );
};

const Side = ({
    title,
    columns,
    nodes,
    totalLabel,
    totalCurrent,
    totalPrevious,
    extraRow,
    collapseAllSignal,
    dashOnZero,
}) => {
    const [collapsedMap, setCollapsedMap] = useState({});

    // Collect all collapsible IDs for Collapse All
    const allIds = useMemo(() => {
        const ids = [];
        const walk = (arr) => {
            (arr || []).forEach((n) => {
                const ch = getChildren(n);
                if (ch.length > 0) {
                    ids.push(n.id ?? getCode(n));
                    walk(ch);
                }
            });
        };
        walk(nodes);
        return ids;
    }, [nodes]);

    useEffect(() => {
        if (collapseAllSignal === undefined) return;
        // Toggle: if anything is open, collapse all; else expand all.
        const anyOpen = allIds.some((id) => !collapsedMap[id]);
        if (anyOpen) {
            const next = {};
            allIds.forEach((id) => { next[id] = true; });
            setCollapsedMap(next);
        } else {
            setCollapsedMap({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collapseAllSignal]);

    const toggle = (id) => setCollapsedMap((prev) => ({ ...prev, [id]: !prev[id] }));

    return (
        <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-text-xs">
                <thead>
                    <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                        <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">{title}</th>
                        {columns.map((col, i) => (
                            <th key={i} className={`tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-[11px] tw-whitespace-nowrap ${col.muted ? 'tw-text-gray-500' : 'tw-text-gray-700'}`}>
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {(nodes || []).map((node) => (
                        <Row
                            key={node.id ?? getCode(node)}
                            node={node}
                            depth={0}
                            collapsedMap={collapsedMap}
                            onToggle={toggle}
                            dashOnZero={dashOnZero}
                        />
                    ))}
                    {(!nodes || nodes.length === 0) && (
                        <tr>
                            <td colSpan={columns.length + 1} className="tw-text-center tw-py-12 tw-text-xs tw-text-gray-400">
                                No data available
                            </td>
                        </tr>
                    )}
                </tbody>
                {nodes && nodes.length > 0 && (
                    <tfoot>
                        {extraRow && (
                            <tr className="tw-bg-amber-50/40 tw-border-t tw-border-amber-100 tw-font-medium">
                                <td className="tw-px-4 tw-py-2 tw-text-amber-700">{extraRow.label}</td>
                                <td className="tw-px-4 tw-py-2 tw-text-right tw-text-amber-700 tw-whitespace-nowrap">
                                    {formatCurrency(getCurrent(extraRow), { dashOnZero })}
                                </td>
                                <td className="tw-px-4 tw-py-2 tw-text-right tw-text-amber-600 tw-whitespace-nowrap">
                                    {formatCurrency(getPrevious(extraRow), { dashOnZero })}
                                </td>
                            </tr>
                        )}
                        <tr className="tw-bg-gray-100 tw-border-t-2 tw-border-gray-300 tw-font-bold">
                            <td className="tw-px-4 tw-py-3 tw-text-gray-800">{totalLabel}</td>
                            <td className="tw-px-4 tw-py-3 tw-text-right tw-text-gray-900 tw-whitespace-nowrap">
                                {formatCurrency(totalCurrent, { dashOnZero })}
                            </td>
                            <td className="tw-px-4 tw-py-3 tw-text-right tw-text-gray-700 tw-whitespace-nowrap">
                                {formatCurrency(totalPrevious, { dashOnZero })}
                            </td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
};

const TwoColumnReport = ({
    leftTitle,
    rightTitle,
    leftNodes,
    rightNodes,
    columns,
    leftTotalLabel,
    leftTotalCurrent,
    leftTotalPrevious,
    rightTotalLabel,
    rightTotalCurrent,
    rightTotalPrevious,
    leftExtraRow,
    rightExtraRow,
    collapseAllSignal,
    dashOnZero = true,
}) => {
    return (
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 lg:tw-divide-x tw-divide-gray-100">
            <Side
                title={leftTitle}
                columns={columns}
                nodes={leftNodes}
                totalLabel={leftTotalLabel}
                totalCurrent={leftTotalCurrent}
                totalPrevious={leftTotalPrevious}
                extraRow={leftExtraRow}
                collapseAllSignal={collapseAllSignal}
                dashOnZero={dashOnZero}
            />
            <Side
                title={rightTitle}
                columns={columns}
                nodes={rightNodes}
                totalLabel={rightTotalLabel}
                totalCurrent={rightTotalCurrent}
                totalPrevious={rightTotalPrevious}
                extraRow={rightExtraRow}
                collapseAllSignal={collapseAllSignal}
                dashOnZero={dashOnZero}
            />
        </div>
    );
};

export default TwoColumnReport;
